import { create } from "zustand";
import { getWebSocketUrl } from "src/config";
import { apiClient } from "src/services/apiClient";
import { mergeCustomExportLayoutCache } from "src/shared/util/src/custom-disciplines/export-profiles";
import type {
  DisciplineAgeRule,
  DisciplineWeightRule,
} from "src/shared/util/src/custom-disciplines/category-catalog";

interface CategoryReferences {
  organisations?: import("src/shared/util/src/layout/types").OrganisationData[];
  disciplineAgeCategories: DisciplineAgeRule[];
  disciplineWeightCategories: DisciplineWeightRule[];
}

export type {
  CustomDisciplineActionPlacement,
  CustomDisciplineActionIconType,
  CustomDisciplineActionIconGridTarget,
  CustomDisciplineBehavior,
  CustomDisciplineCellLayoutDefinition,
  CustomDiscipline,
  CustomDisciplineFieldDefinition,
  CustomDisciplineFieldOption,
  CustomDisciplineFieldScope,
  CustomDisciplineFieldType,
  CustomDisciplineGroupActionDefinition,
  CustomDisciplineGroupModalDefinition,
  CustomDisciplineGroupType,
  CustomDisciplineGroupTypeInput,
  CustomDisciplineInput,
  CustomDisciplineLabelTranslation,
  CustomDisciplineLabelTranslationInput,
  CustomDisciplineReferenceSource,
  CustomDisciplineTranslationImportPayload,
} from "src/shared/util/src/custom-disciplines";

export { emptyBehavior } from "src/shared/util/src/custom-disciplines";

import type {
  CustomDiscipline,
  CustomDisciplineGroupType,
  CustomDisciplineGroupTypeInput,
  CustomDisciplineInput,
  CustomDisciplineLabelTranslation,
  CustomDisciplineLabelTranslationInput,
  CustomDisciplineTranslationImportPayload,
} from "src/shared/util/src/custom-disciplines";

interface CustomDisciplineState {
  disciplines: CustomDiscipline[];
  groupTypes: CustomDisciplineGroupType[];
  labelTranslations: CustomDisciplineLabelTranslation[];
  categoryReferences: CategoryReferences;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchAll: (includeExportProfiles?: boolean) => Promise<void>;
  createDiscipline: (data: CustomDisciplineInput) => Promise<void>;
  updateDiscipline: (
    id: string,
    data: Partial<CustomDisciplineInput>,
  ) => Promise<void>;
  deleteDiscipline: (id: string) => Promise<void>;
  createGroupType: (
    data: CustomDisciplineGroupTypeInput,
  ) => Promise<CustomDisciplineGroupType>;
  updateGroupType: (
    id: string,
    data: Partial<CustomDisciplineGroupTypeInput>,
  ) => Promise<CustomDisciplineGroupType>;
  deleteGroupType: (id: string) => Promise<void>;
  createLabelTranslation: (
    data: CustomDisciplineLabelTranslationInput,
  ) => Promise<void>;
  updateLabelTranslation: (
    id: string,
    data: Partial<CustomDisciplineLabelTranslationInput>,
  ) => Promise<void>;
  deleteLabelTranslation: (id: string) => Promise<void>;
  importLabelTranslations: (
    data: CustomDisciplineTranslationImportPayload,
  ) => Promise<void>;
  setError: (error: string | null) => void;
}

function upsertGroupType(
  groupTypes: CustomDisciplineGroupType[],
  groupType: CustomDisciplineGroupType,
): CustomDisciplineGroupType[] {
  const exists = groupTypes.some((item) => item.id === groupType.id);
  if (!exists) {
    return [...groupTypes, groupType];
  }
  return groupTypes.map((item) =>
    item.id === groupType.id ? groupType : item,
  );
}

function withGroupTypeOnDisciplines(
  disciplines: CustomDiscipline[],
  groupType: CustomDisciplineGroupType,
): CustomDiscipline[] {
  return disciplines.map((discipline) => {
    if (discipline.id === groupType.disciplineId) {
      return { ...discipline, groupType };
    }
    if (discipline.groupType?.id === groupType.id) {
      return { ...discipline, groupType: undefined };
    }
    return discipline;
  });
}

let latestFetchId = 0;

export const useCustomDisciplineStore = create<CustomDisciplineState>(
  (set, get) => ({
    disciplines: [],
    groupTypes: [],
    labelTranslations: [],
    categoryReferences: {
      disciplineAgeCategories: [],
      disciplineWeightCategories: [],
    },
    loading: false,
    saving: false,
    error: null,

    fetchAll: async (includeExportProfiles = false) => {
      const requestId = ++latestFetchId;
      const isInitialLoad =
        get().disciplines.length === 0 &&
        get().groupTypes.length === 0 &&
        get().labelTranslations.length === 0;
      set(isInitialLoad ? { loading: true, error: null } : { error: null });
      try {
        const {
          groupTypes,
          disciplines,
          labelTranslations,
          categoryReferences,
        } = await apiClient.get<{
          groupTypes: CustomDisciplineGroupType[];
          disciplines: CustomDiscipline[];
          labelTranslations: CustomDisciplineLabelTranslation[];
          categoryReferences?: {
            organisations?: import("src/shared/util/src/layout/types").OrganisationData[];
            ageCategories: DisciplineAgeRule[];
            weightCategories: DisciplineWeightRule[];
          };
        }>(
          includeExportProfiles
            ? "/custom-discipline-schema/snapshot/licensed"
            : "/custom-discipline-schema/snapshot",
          { requiresAuth: includeExportProfiles },
        );
        if (requestId !== latestFetchId) return;
        set({
          groupTypes: includeExportProfiles
            ? groupTypes
            : groupTypes.map((type) => ({
                ...type,
                displayLayouts: mergeCustomExportLayoutCache(
                  type.displayLayouts,
                  get().groupTypes.find((cached) => cached.id === type.id)
                    ?.displayLayouts,
                  false,
                ) as CustomDisciplineGroupType["displayLayouts"],
              })),
          disciplines,
          labelTranslations,
          loading: false,
          categoryReferences: {
            organisations: (categoryReferences?.organisations ?? []).map(
              (organisation) => ({
                ...organisation,
                recordBadgeUrl: organisation.recordBadgeUrl?.startsWith("/")
                  ? new URL(organisation.recordBadgeUrl, getWebSocketUrl()).href
                  : organisation.recordBadgeUrl,
              }),
            ),
            disciplineAgeCategories: categoryReferences?.ageCategories ?? [],
            disciplineWeightCategories:
              categoryReferences?.weightCategories ?? [],
          },
        });
      } catch (error) {
        if (requestId !== latestFetchId) return;
        set({
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINES_FETCH_ERROR",
        });
      }
    },

    createDiscipline: async (data) => {
      set({ saving: true, error: null });
      try {
        await apiClient.post<CustomDiscipline>("/custom-disciplines", data);
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_CREATE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    updateDiscipline: async (id, data) => {
      set({ saving: true, error: null });
      try {
        await apiClient.patch<CustomDiscipline>(
          `/custom-disciplines/${id}`,
          data,
        );
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_UPDATE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    deleteDiscipline: async (id) => {
      set({ saving: true, error: null });
      try {
        await apiClient.delete<void>(`/custom-disciplines/${id}`);
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_DELETE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    createGroupType: async (data) => {
      set({ saving: true, error: null });
      try {
        const groupType = await apiClient.post<CustomDisciplineGroupType>(
          "/custom-discipline-group-types",
          data,
        );
        const state = get();
        set({
          saving: false,
          groupTypes: upsertGroupType(state.groupTypes, groupType),
          disciplines: withGroupTypeOnDisciplines(state.disciplines, groupType),
        });
        return groupType;
      } catch (error) {
        set({
          saving: false,
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_GROUP_TYPE_CREATE_ERROR",
        });
        throw error;
      }
    },

    updateGroupType: async (id, data) => {
      set({ saving: true, error: null });
      try {
        const groupType = await apiClient.patch<CustomDisciplineGroupType>(
          `/custom-discipline-group-types/${id}`,
          data,
        );
        const state = get();
        set({
          saving: false,
          groupTypes: upsertGroupType(state.groupTypes, groupType),
          disciplines: withGroupTypeOnDisciplines(state.disciplines, groupType),
        });
        return groupType;
      } catch (error) {
        set({
          saving: false,
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_GROUP_TYPE_UPDATE_ERROR",
        });
        throw error;
      }
    },

    deleteGroupType: async (id) => {
      set({ saving: true, error: null });
      try {
        await apiClient.delete<void>(`/custom-discipline-group-types/${id}`);
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_GROUP_TYPE_DELETE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    createLabelTranslation: async (data) => {
      set({ saving: true, error: null });
      try {
        await apiClient.post<CustomDisciplineLabelTranslation>(
          "/custom-discipline-label-translations",
          data,
        );
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_TRANSLATION_CREATE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    updateLabelTranslation: async (id, data) => {
      set({ saving: true, error: null });
      try {
        await apiClient.patch<CustomDisciplineLabelTranslation>(
          `/custom-discipline-label-translations/${id}`,
          data,
        );
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_TRANSLATION_UPDATE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    deleteLabelTranslation: async (id) => {
      set({ saving: true, error: null });
      try {
        await apiClient.delete<void>(
          `/custom-discipline-label-translations/${id}`,
        );
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_TRANSLATION_DELETE_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    importLabelTranslations: async (data) => {
      set({ saving: true, error: null });
      try {
        await apiClient.post<CustomDisciplineLabelTranslation[]>(
          "/custom-discipline-label-translations/import",
          data,
        );
        void get().fetchAll(true);
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : "CUSTOM_DISCIPLINE_TRANSLATION_IMPORT_ERROR",
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    setError: (error) => set({ error }),
  }),
);
